<template>
  <b-field label="Answer" :class="$style.container">
    <div>
      <div class="columns">
        <div class="column">
          <ValidationProvider
            v-slot="{ errors }"
            :rules="`required`"
            name="Min Value"
          >
            <b-field
              :type="errors.length ? 'is-danger' : undefined"
              :message="errors[0]"
            >
              <div :class="$style.inputContainer">
                <div :class="$style.label">Min</div>
                <b-input
                  v-model="start"
                  type="number"
                  onkeydown="if(event.key==='.'){event.preventDefault();}"
                />
              </div>
            </b-field>
            <p v-if="minRangeError" :class="$style.validationErrorMessage">
              Min Value cannot be greater than Max Value
            </p>
          </ValidationProvider>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <ValidationProvider
            v-slot="{ errors }"
            :rules="`required`"
            name="Max Value"
          >
            <b-field
              :type="errors.length ? 'is-danger' : undefined"
              :message="errors[0]"
            >
              <div :class="$style.inputContainer">
                <div :class="$style.label">Max</div>
                <b-input
                  v-model="end"
                  type="number"
                  onkeydown="if(event.key==='.'){event.preventDefault();}"
                />
              </div>
            </b-field>
            <p v-if="maxRangeError" :class="$style.validationErrorMessage">
              Max Value cannot be lesser than Min Value
            </p>
          </ValidationProvider>
        </div>
      </div>
      <div class="columns">
        <div class="column">
          <ValidationProvider
            v-slot="{ errors }"
            :rules="`required`"
            name="Not Applicable Value"
          >
            <b-field
              :type="errors.length ? 'is-danger' : undefined"
              :message="errors[0]"
            >
              <div :class="$style.inputContainer">
                <div :class="$style.label">N/A</div>
                <b-input v-model="notApplicable" disabled />
              </div>
            </b-field>
          </ValidationProvider>
        </div>
      </div>
    </div>
  </b-field>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { RangeCriterionContent } from '@/store/types/criterions.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';

@Component({
  components: { ValidationProvider, ValidationObserver },
})
export default class RangeCriterionInput extends Vue {
  @Prop({
    default() {
      return [1, 10, 'N/A'];
    },
  })
  public range!: RangeCriterionContent['range'];

  public start!: number | string;
  public end!: number | string;
  public notApplicable!: string;
  public minRangeError!: boolean;
  public maxRangeError!: boolean;

  public data(): {
    start: number | string;
    end: number | string;
    notApplicable: string;
    minRangeError: boolean;
    maxRangeError: boolean;
  } {
    const values = this.range ?? [1, 10, 'N/A'];
    return {
      start: values[0],
      end: values[1],
      notApplicable: String(values[2]),
      minRangeError: false,
      maxRangeError: false,
    };
  }

  private syncFromRange(): void {
    const values = this.range ?? [1, 10, 'N/A'];
    this.start = values[0];
    this.end = values[1];
    this.notApplicable = String(values[2]);
  }

  @Watch('start')
  public onStartChanged() {
    this.minRangeError =
      this.start.toString() !== '' && Number(this.start) >= Number(this.end)
        ? true
        : false;
    if (this.maxRangeError) {
      this.maxRangeError = this.minRangeError;
    }
    this.$emit('update:range', [this.start, this.end, this.notApplicable]);
    this.$emit('disableSubmitButton', this.minRangeError);
  }

  @Watch('end')
  public onEndChanged() {
    this.maxRangeError =
      this.end.toString() !== '' && Number(this.end) <= Number(this.start)
        ? true
        : false;
    if (this.minRangeError) {
      this.minRangeError = this.maxRangeError;
    }
    this.$emit('update:range', [this.start, this.end, this.notApplicable]);
    this.$emit('disableSubmitButton', this.maxRangeError);
  }

  @isTruthy
  @Watch('range')
  public onRangeUpdated() {
    this.syncFromRange();
  }
}
</script>

<style lang="scss" module>
.container {
  &#{&} {
    margin-bottom: 0;
  }

  :global .field {
    margin-bottom: 6px;
  }
}

.inputContainer {
  display: flex;
  align-items: center;

  .label {
    padding-right: 18px;
    width: 46px;
    color: $grey;
    font-size: 14px;
    font-weight: 600;
  }

  :global .control {
    flex: 1 1 100%;
  }

  + :global(.help) {
    margin-left: 50px;
  }
}

.validationErrorMessage {
  color: red;
  font-size: 12px;
  padding-bottom: 10px;
  padding-left: 45px;
}
</style>
